import { useState } from "react";
import { useFetch } from "../hooks/useFetch";
import {
  createSubscriptionByAdmin,
  renewSubscriptionByAdmin,
  verifyPaymentByAdmin,
  getDriverSubscriptionByAdmin,
} from "../api/subscriptionsApi";
import { getAllPlanByAdmin } from "../api/planApi";
import { getDriverById } from "../api/driverApi";
import "./css/Subscriptions.css";
import AccessControl from "../components/AccessControl";

// ─── Load Razorpay script dynamically ────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function AgentActivatePage() {
  // ── Plans (fetched once) ────────────────────────────────────────────────
  const { data: planData } = useFetch(getAllPlanByAdmin, []);
  const plans = planData?.plans ?? (Array.isArray(planData) ? planData : []);
  const activePlans = plans.filter((p) => p.isActive);

  // ── Search state ────────────────────────────────────────────────────────
  const [searchId,  setSearchId]  = useState("");
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");

  // ── Driver / Activation state ───────────────────────────────────────────
  const [driver,        setDriver]        = useState(null); // { _id, name, phone, zoneId, zoneName, subStatus }
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [method,         setMethod]         = useState("cash");
  const [busy,           setBusy]           = useState(false);
  const [toast,          setToast]          = useState("");

  const showToast = (msg, ms = 4000) => {
    setToast(msg);
    setTimeout(() => setToast(""), ms);
  };

  // ── Normalize search input — 10-digit phone gets +91 auto-prefixed ─────────
  // Driver IDs / Mongo _id (letters + numbers) pass through untouched.
  const normalizePhoneOrId = (raw) => {
    const val = raw.trim();
    if (!val || val.startsWith("+")) return val;

    const digitsOnly = val.replace(/[\s-]/g, "");

    // Plain 10-digit mobile number → +91XXXXXXXXXX
    if (/^\d{10}$/.test(digitsOnly)) return `+91${digitsOnly}`;

    // 91XXXXXXXXXX (missing the +) → +91XXXXXXXXXX
    if (/^91\d{10}$/.test(digitsOnly)) return `+${digitsOnly}`;

    // Not phone-shaped — leave as Driver ID / _id
    return val;
  };

  // ── Search driver by ID ─────────────────────────────────────────────────
  const handleSearch = async () => {
    const id = normalizePhoneOrId(searchId);
    if (!id) return setSearchErr("⚠️ Driver ID / Phone போடுங்க");

    setSearching(true);
    setSearchErr("");
    setDriver(null);
    setSelectedPlanId("");

    try {
      const res = await getDriverById(id);
      if (!res?.success) {
        throw new Error(res?.error || res?.message || "Driver கிடைக்கல");
      }

      const d = res.data ?? res.driver ?? res;
      if (!d?._id) throw new Error("Driver கிடைக்கல");

      // ✅ Real subscription status — Driver.status is operational (online/offline etc),
      // NOT subscription status. Fetch the actual subscription record to know
      // whether this is a fresh ACTIVATE or a RENEW.
      let subStatus     = "inactive";
      let renewedFrom   = null;
      try {
        const subRes = await getDriverSubscriptionByAdmin(d._id);
        if (subRes?.success) {
          const sub = subRes.subscription ?? subRes.data ?? subRes;
          subStatus   = sub?.status || "inactive";
          renewedFrom = sub?.renewedFrom || null;
        }
        // subRes.success === false (e.g. "No subscription found") → stays "inactive",
        // which correctly routes to ACTIVATE (create), not renew.
      } catch {
        subStatus = "inactive";
      }

      setDriver({
        _id:       d._id,
        name:      d.name || "—",
        phone:     d.phone || "",
        zoneId:    d.zoneId || null,
        zoneName:  d.zoneName || "",
        subStatus,
        // ✅ "pending" record that was created FROM a renewal (renewedFrom set)
        // must retry via renew, not initiate — initiate's own active-check would
        // find the still-valid old subscription and block with "already active".
        isRenewalInProgress: !!renewedFrom,
      });
    } catch (e) {
      setSearchErr(`❌ ${e.message}`);
    } finally {
      setSearching(false);
    }
  };

  const resetSearch = () => {
    setDriver(null);
    setSearchId("");
    setSearchErr("");
    setSelectedPlanId("");
    setMethod("cash");
  };

  // ── Activate / Renew handler ────────────────────────────────────────────
  const handleActivate = async () => {
    if (!selectedPlanId) return showToast("⚠️ Plan select பண்ணுங்க");

    const isRenew =
      driver.subStatus === "active" ||
      (driver.subStatus === "pending" && driver.isRenewalInProgress);
    const apiFn   = isRenew ? renewSubscriptionByAdmin : createSubscriptionByAdmin;

    setBusy(true);
    try {
      const agentId = localStorage.getItem("adminId");

      const res = await apiFn({
        driverId:    driver._id,
        planId:      selectedPlanId,
        method,
        collectedBy: agentId || undefined, // ✅ logged-in agent
      });

      if (!res?.success) throw new Error(res?.error || res?.message || "Failed");

      const d = res.data;

      // ── CASH ───────────────────────────────────────────────────────────
      if (method === "cash") {
        showToast("✅ Cash payment pending. Admin confirm பண்ணா activate ஆகும்.");
        resetSearch();
        return;
      }

      // ── ONLINE: open Razorpay checkout ───────────────────────────────────
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        showToast("❌ Razorpay load ஆகல. Internet check பண்ணுங்க.");
        setBusy(false);
        return;
      }

      const plan = activePlans.find((p) => p._id === selectedPlanId);

      const options = {
        key:         d.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount:      d.amount * 100,
        currency:    "INR",
        name:        "Moovuruli",
        description: `${plan?.name || "Plan"} — ${driver.name}`,
        order_id:    d.razorpayOrderId,
        prefill: {
          name:    driver.name,
          contact: driver.phone,
        },
        theme: { color: "#7c3aed" },

        handler: async (response) => {
          try {
            const verifyRes = await verifyPaymentByAdmin({
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            if (verifyRes?.success) {
              showToast("✅ Payment successful! Subscription activated.");
              resetSearch();
            } else {
              showToast(`❌ Verify failed: ${verifyRes?.message || "Unknown error"}`);
            }
          } catch (e) {
            showToast(`❌ Verification error: ${e.message}`);
          } finally {
            setBusy(false);
          }
        },

        modal: {
          ondismiss: () => {
            showToast("ℹ️ Payment cancelled.");
            setBusy(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        showToast(`❌ Payment failed: ${resp.error?.description || "Try again"}`);
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      showToast(`❌ ${e.message}`);
      setBusy(false);
    }
  };

  const getBadgeClass = (val) => {
    const map = {
      active:  "badge badge-active",
      expired: "badge badge-expired",
      pending: "badge badge-pending",
    };
    return map[val] || "badge badge-default";
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="subs-container">
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "AGENT"]}
        redirectTo="/404"
      />

      {toast && <div className="pc-toast">{toast}</div>}

      <div className="subs-header">
        <h1>Agent — Activate Plan</h1>
        <p>Driver ID / Phone search பண்ணி plan activate பண்ணலாம்</p>
      </div>

      {/* ═══════════ SEARCH BOX ═══════════ */}
      {!driver && (
        <div className="pc-detail-card" style={{ maxWidth: 480 }}>
          <label className="pc-notes-label" style={{ display: "block", marginBottom: "0.5rem" }}>
            Driver ID / Phone <span className="pc-required">*</span>
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <input
              className="pc-notes-input"
              style={{ flex: 1, padding: "0.5rem", borderRadius: "6px" }}
              placeholder="Driver ID அல்லது Phone (10 digit போதும், +91 auto-ஆ add ஆகும்)"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              className="pc-btn-activate"
              onClick={handleSearch}
              disabled={searching}
            >
              {searching ? "Searching…" : "🔍 Search"}
            </button>
          </div>
          {searchErr && (
            <p style={{ color: "#dc2626", fontSize: "0.8rem", marginTop: "0.5rem" }}>
              {searchErr}
            </p>
          )}
        </div>
      )}

      {/* ═══════════ DRIVER FOUND → CONFIRM + ACTIVATE ═══════════ */}
      {driver && (
        <div className="pc-modal-card" style={{ maxWidth: 480, marginTop: "1rem" }}>
          <div className="pc-modal-header">
            <h3>
              {(driver.subStatus === "active" || (driver.subStatus === "pending" && driver.isRenewalInProgress))
                ? "🔄 Renew Subscription"
                : "⚡ Activate Subscription"}
            </h3>
            <button className="pc-modal-close" onClick={resetSearch}>×</button>
          </div>

          <div className="pc-modal-body">
            {/* Driver info — confirm before activating */}
            <div className="pc-detail-card">
              <div className="pc-detail-row">
                <span className="pc-detail-label">Driver</span>
                <span className="pc-detail-value">{driver.name}</span>
              </div>
              <div className="pc-detail-row">
                <span className="pc-detail-label">Phone</span>
                <span className="pc-detail-value">{driver.phone}</span>
              </div>
              <div className="pc-detail-row">
                <span className="pc-detail-label">Territory</span>
                <span className="pc-detail-value">{driver.zoneName || "—"}</span>
              </div>
              <div className="pc-detail-row">
                <span className="pc-detail-label">Current Status</span>
                <span className={`badge ${getBadgeClass(driver.subStatus)}`}>
                  {driver.subStatus}
                </span>
              </div>
            </div>
            <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "0.5rem 0" }}>
              👆 Driver details confirm பண்ணுங்க, தப்பா இருந்தா × click பண்ணி மறுபடி search பண்ணுங்க
            </p>

            {/* Plan Select */}
            <label className="pc-notes-label" style={{ marginTop: "1rem", display: "block" }}>
              Select Plan <span className="pc-required">*</span>
            </label>
            <select
              className="pc-notes-input"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              style={{ padding: "0.5rem", borderRadius: "6px", width: "100%" }}
            >
              <option value="">-- Plan தேர்வு செய்யுங்க --</option>
              {activePlans.map((p) => {
                const isTrial = /trai[ll]/i.test(p.name);
                const zoneOverride = driver.zoneId
                  ? p.zonePrices?.find((z) => z.zoneId === driver.zoneId)
                  : null;
                const effectiveAmt = zoneOverride ? zoneOverride.amount : p.amount;
                const isOverridden = !!zoneOverride && zoneOverride.amount !== p.amount;
                return (
                  <option key={p._id} value={p._id} disabled={isTrial}>
                    {p.name} — ₹{effectiveAmt?.toLocaleString()}
                    {isOverridden ? ` (zone | global: ₹${p.amount?.toLocaleString()})` : ""}
                    {" "}({p.value} {p.unit})
                    {isTrial ? " (Not allowed)" : ""}
                  </option>
                );
              })}
            </select>

            {/* Payment Method */}
            <label className="pc-notes-label" style={{ marginTop: "1rem", display: "block" }}>
              Payment Method
            </label>
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
              {["cash", "online"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMethod(m)}
                  className={`filter-pill ${method === m ? "active" : ""}`}
                  style={{ flex: 1, textAlign: "center" }}
                >
                  {m === "cash" ? "💵 Cash" : "💳 Online (Razorpay)"}
                </button>
              ))}
            </div>

            {method === "cash" && (
              <div className="pc-info-banner" style={{ marginBottom: "1rem" }}>
                <span>💡</span>
                <p>
                  Cash pending record create ஆகும். Admin "Confirm Cash" பண்ணா
                  subscription activate ஆகும்.
                </p>
              </div>
            )}
            {method === "online" && (
              <div className="pc-info-banner" style={{ marginBottom: "1rem" }}>
                <span>ℹ️</span>
                <p>Razorpay checkout open ஆகும். Payment complete ஆனதும் subscription automatically activate ஆகும்.</p>
              </div>
            )}

            {/* Actions */}
            <div className="pc-modal-actions">
              <button className="pc-btn-cancel" onClick={resetSearch} disabled={busy}>
                Cancel
              </button>
              <button
                className="pc-btn-activate"
                onClick={handleActivate}
                disabled={busy || !selectedPlanId}
              >
                {busy
                  ? "Processing…"
                  : method === "cash"
                  ? "✅ Create Cash Record"
                  : "💳 Open Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}